import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Command, CommandInput, CommandList, CommandGroup, CommandItem, CommandEmpty } from '@/components/ui/command';
import api from '@/lib/api';
import {
  Users, FolderKanban, FileSpreadsheet, Receipt, CheckSquare, Image, UserCircle, Clock, Search,
} from 'lucide-react';

const RECENT_KEY = 'is_recent_views';
const TYPE_META = {
  lead: { icon: Users, badgeCls: 'bg-[#C67D5B]/10 text-[#C67D5B]' },
  project: { icon: FolderKanban, badgeCls: 'bg-[#3A8C8C]/10 text-[#3A8C8C]' },
  boq: { icon: FileSpreadsheet, badgeCls: 'bg-[#E8E6E1] text-[#6B7280]' },
  bill: { icon: Receipt, badgeCls: 'bg-[#7DA68A]/10 text-[#7DA68A]' },
  task: { icon: CheckSquare, badgeCls: 'bg-[#E8E6E1] text-[#6B7280]' },
  drawing: { icon: Image, badgeCls: 'bg-[#E8E6E1] text-[#6B7280]' },
  team: { icon: UserCircle, badgeCls: 'bg-[#3A8C8C]/10 text-[#3A8C8C]' },
};

export function getRecentlyViewed() {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch { return []; }
}
function pushRecent(item) {
  const slim = { type: item.type, id: item.id, title: item.title, subtitle: item.subtitle, badge: item.badge, route: item.route, params: item.params };
  const list = getRecentlyViewed().filter(r => !(r.type === item.type && r.id === item.id));
  list.unshift(slim);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 5)));
}
function logSearchSelection(query, item) {
  // Phase 1: lightweight local log (Phase 2 will surface popular searches from backend analytics)
  try {
    const logs = JSON.parse(localStorage.getItem('is_search_logs') || '[]');
    logs.unshift({ q: query, type: item.type, id: item.id, at: Date.now() });
    localStorage.setItem('is_search_logs', JSON.stringify(logs.slice(0, 50)));
  } catch { /* noop */ }
}
function toUrl(item) {
  const p = new URLSearchParams(item.params || {}).toString();
  return item.route + (p ? `?${p}` : '');
}

function ResultRow({ item, query, onSelect }) {
  const meta = TYPE_META[item.type] || TYPE_META.project;
  const Icon = meta.icon;
  return (
    <CommandItem
      value={`${item.type}-${item.id}`}
      onSelect={() => onSelect(item, query)}
      className="h-14 px-3 rounded-lg cursor-pointer aria-selected:bg-[#FAFAF8] data-[selected=true]:bg-[#FAFAF8]"
      data-testid={`search-result-${item.type}-${item.id}`}
    >
      <div className="w-8 h-8 rounded-lg bg-[#FAFAF8] flex items-center justify-center shrink-0">
        <Icon size={18} strokeWidth={1.5} className="text-[#3A8C8C]" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#2C2C2E] truncate font-medium">{item.title}</p>
        {item.subtitle && <p className="text-xs text-[#6B7280] truncate">{item.subtitle}</p>}
      </div>
      {item.badge && <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize shrink-0 ${meta.badgeCls}`}>{item.badge}</span>}
      {item.meta && <span className="text-[10px] text-[#9CA3AF] shrink-0 ml-1">{item.meta}</span>}
    </CommandItem>
  );
}

export default function CommandPalette({ open, onOpenChange, initialQuery = '' }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const debounceRef = useRef(null);
  const reqRef = useRef(0);

  const searchAllEntities = useCallback(async (q) => {
    const myReq = ++reqRef.current;
    setLoading(true);
    try {
      const res = await api.get(`/search?q=${encodeURIComponent(q)}`);
      if (myReq === reqRef.current) setGroups(res.data.groups || []);
    } catch {
      if (myReq === reqRef.current) setGroups([]);
    } finally {
      if (myReq === reqRef.current) setLoading(false);
    }
  }, []);

  const handleSearchQueryChange = (val) => {
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!val.trim()) { setGroups([]); setLoading(false); return; }
    debounceRef.current = setTimeout(() => searchAllEntities(val.trim()), 200);
  };

  useEffect(() => {
    if (open) {
      const seed = initialQuery || '';
      setQuery(seed); setGroups([]); setLoading(false);
      setRecent(getRecentlyViewed());
      if (seed.trim()) searchAllEntities(seed.trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleResultSelect = (item, q) => {
    pushRecent(item);
    if (q) logSearchSelection(q, item);
    onOpenChange(false);
    navigate(toUrl(item));
  };

  const showRecent = !query.trim();
  const hasResults = groups.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="p-0 gap-0 max-w-[640px] overflow-hidden rounded-xl border-[#E8E6E1] top-[14%] translate-y-0 shadow-2xl"
        data-testid="command-palette"
      >
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <DialogDescription className="sr-only">Search leads, projects, bills, tasks, drawings and team members.</DialogDescription>
        <Command shouldFilter={false} className="bg-white" loop>
          <div className="flex items-center border-b border-[#E8E6E1] px-4">
            <Search size={20} strokeWidth={1.5} className="text-[#6B7280] shrink-0" />
            <CommandInput
              value={query}
              onValueChange={handleSearchQueryChange}
              placeholder="Search leads, projects, bills, tasks..."
              className="h-14 text-[17px] placeholder:text-[#9CA3AF] border-0"
              data-testid="command-palette-input"
            />
            <kbd className="hidden sm:inline text-[10px] text-[#9CA3AF] border border-[#E8E6E1] rounded px-1.5 py-0.5">ESC</kbd>
          </div>

          <CommandList className="max-h-[420px] p-2">
            {/* Loading skeleton */}
            {loading && (
              <div className="space-y-2 p-1" data-testid="search-loading">
                {[0, 1, 2].map(i => (
                  <div key={i} className="h-14 rounded-lg bg-[#FAFAF8] animate-pulse" />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!loading && query.trim() && !hasResults && (
              <div className="py-12 text-center" data-testid="search-empty">
                <Search size={28} strokeWidth={1.5} className="mx-auto text-[#D1D5DB] mb-3" />
                <p className="text-sm text-[#6B7280]">No results for "<span className="text-[#2C2C2E] font-medium">{query}</span>"</p>
                <p className="text-xs text-[#9CA3AF] mt-1">Check your spelling or try a different term.</p>
              </div>
            )}

            {/* Recent (empty query) */}
            {!loading && showRecent && recent.length > 0 && (
              <CommandGroup
                heading={<span className="text-[11px] uppercase tracking-wide text-[#2C2C2E]/60 font-semibold flex items-center gap-1.5" data-testid="search-group-recent"><Clock size={11} strokeWidth={2} /> Recent</span>}
              >
                {recent.map(item => <ResultRow key={`recent-${item.type}-${item.id}`} item={item} query="" onSelect={handleResultSelect} />)}
              </CommandGroup>
            )}

            {!loading && showRecent && recent.length === 0 && (
              <div className="py-12 text-center" data-testid="search-hint">
                <p className="text-sm text-[#6B7280]">Search across leads, projects, BOQs, bills, tasks, drawings &amp; team.</p>
                <p className="text-xs text-[#9CA3AF] mt-1">Press <kbd className="text-[10px] border border-[#E8E6E1] rounded px-1">↑</kbd> <kbd className="text-[10px] border border-[#E8E6E1] rounded px-1">↓</kbd> to navigate, <kbd className="text-[10px] border border-[#E8E6E1] rounded px-1">↵</kbd> to open.</p>
              </div>
            )}

            {/* Results grouped */}
            {!loading && !showRecent && groups.map(g => (
              <CommandGroup
                key={g.key}
                heading={
                  <div className="flex items-center justify-between pr-1">
                    <span className="text-[11px] uppercase tracking-wide text-[#2C2C2E]/60 font-semibold" data-testid={`search-group-${g.key}`}>{g.label}</span>
                    {g.count > g.items.length && (
                      <span className="text-[10px] text-[#3A8C8C]">{g.count} results</span>
                    )}
                  </div>
                }
              >
                {g.items.map(item => <ResultRow key={`${item.type}-${item.id}`} item={item} query={query} onSelect={handleResultSelect} />)}
              </CommandGroup>
            ))}
            <CommandEmpty className="hidden" />
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
